/**
 * PageDefinition v3 (#2286).
 *
 * One closed, data-only composition document answers what a governed
 * destination presents.  It arranges identities owned by existing registries;
 * it does not redefine field meaning, relationship metadata, authority, React
 * components, SQL, URLs or executable expressions.  The same document can
 * therefore be drafted by the visual editor, AI or MCP and pass through this
 * one boundary before the existing page writer publishes it.
 *
 * V1/v2 page compositions remain readable in `page.ts`.  V3 is deliberately a
 * separate shape: sections contain rows, rows contain columns, and columns
 * contain blocks.  No client has to infer a row by grouping adjacent spans.
 */

import {
  exactKeys as exact, integerInRange as integer, isRecord as record,
} from './documentShape.js'

export const PAGE_DEFINITION_SCHEMA_VERSION = 3 as const

export const PAGE_DEFINITION_BLOCK_KINDS = [
  'field', 'form', 'view', 'relation', 'dashboard', 'timeline', 'process',
  'actions', 'content', 'widget',
] as const
export type PageDefinitionBlockKind = typeof PAGE_DEFINITION_BLOCK_KINDS[number]

export const PAGE_FIELD_INPUTS = [
  'text', 'textarea', 'number', 'money', 'date', 'datetime', 'email', 'tel',
  'url', 'select', 'multiselect', 'switch', 'lookup',
] as const
export type PageFieldInput = typeof PAGE_FIELD_INPUTS[number]

export const PAGE_VALUE_FORMATS = [
  'text', 'number', 'currency', 'percent', 'date', 'datetime',
] as const
export type PageValueFormat = typeof PAGE_VALUE_FORMATS[number]

export interface PageDefinitionSpan {
  desktop: 1 | 2 | 3 | 4
  tablet: 1 | 2 | 3 | 4
  phone: 1 | 2
}

export interface PageDefinitionRuleRef {
  /** Opaque registered rule identity. Never an expression evaluated by a host. */
  rule: string
}

export interface PageFieldFormatting {
  kind: PageValueFormat
  /** ISO 4217, and admitted only when kind=currency. */
  currency?: string
  precision?: number
}

export interface PageFieldPresentation {
  /** Presentation only. The metamodel remains authority on the field's type. */
  input?: PageFieldInput
  format?: PageFieldFormatting
}

interface PageDefinitionBlockBase {
  id: string
  kind: PageDefinitionBlockKind
  hidden?: boolean
  when?: PageDefinitionRuleRef
}

export interface PageFieldBlock extends PageDefinitionBlockBase {
  kind: 'field'
  entity: string
  field: string
  presentation?: PageFieldPresentation
}

export interface PageFormBlock extends PageDefinitionBlockBase {
  kind: 'form'
  entity: string
  form: string
  /** Optional registered section titles/keys; field meaning stays in the form. */
  sections?: string[]
}

export interface PageViewBlock extends PageDefinitionBlockBase {
  kind: 'view'
  entity: string
  view: string
}

export interface PageRelationBlock extends PageDefinitionBlockBase {
  kind: 'relation'
  entity: string
  /** Registered relationship slot; its presentation is owned by #2276/#2281. */
  slot: string
}

export interface PageDashboardBlock extends PageDefinitionBlockBase {
  kind: 'dashboard'
  dashboard: string
}

export interface PageTimelineBlock extends PageDefinitionBlockBase {
  kind: 'timeline'
  entity: string
  timeline: string
}

export interface PageProcessBlock extends PageDefinitionBlockBase {
  kind: 'process'
  entity: string
  process: string
}

export interface PageActionsBlock extends PageDefinitionBlockBase {
  kind: 'actions'
  entity: string
  group: 'primary' | 'secondary' | 'bulk'
}

export interface PageContentBlock extends PageDefinitionBlockBase {
  kind: 'content'
  style: 'heading' | 'helper' | 'callout'
  text: { en: string; fr?: string; nl?: string }
}

export interface PageWidgetBlockV3 extends PageDefinitionBlockBase {
  kind: 'widget'
  dashboard: string
  item: string
}

export type PageDefinitionBlock =
  | PageFieldBlock
  | PageFormBlock
  | PageViewBlock
  | PageRelationBlock
  | PageDashboardBlock
  | PageTimelineBlock
  | PageProcessBlock
  | PageActionsBlock
  | PageContentBlock
  | PageWidgetBlockV3

export interface PageDefinitionColumn {
  id: string
  span: PageDefinitionSpan
  blocks: PageDefinitionBlock[]
}

export interface PageDefinitionRow {
  id: string
  columns: PageDefinitionColumn[]
}

export interface PageDefinitionSection {
  id: string
  label: string
  description: string | null
  rows: PageDefinitionRow[]
}

export interface PageDefinitionDependency {
  kind: 'entity' | 'field' | 'relation' | 'form' | 'view' | 'dashboard' | 'widget'
    | 'timeline' | 'process' | 'action' | 'rule' | 'control'
  key: string
  version: number | string | null
}

export interface PageDefinition {
  schemaVersion: typeof PAGE_DEFINITION_SCHEMA_VERSION
  id: string
  revision: number
  surface: 'page'
  sections: PageDefinitionSection[]
  unplaced: PageDefinitionBlock[]
  dependencies: PageDefinitionDependency[]
}

const IDENTIFIER = /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/u
const RULE_REFERENCE = /^[a-z][a-z0-9._-]*\/[a-z][a-z0-9._-]*$/u
const PAGE_KEY = /^(console|portal)\.[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/u
const CURRENCY = /^[A-Z]{3}$/u
const NAMED_PRESET = /^[^\p{Cc}\p{Cf}]{1,120}$/u
const OPAQUE_SLOT = /^[^\p{Cc}\p{Cf}]{1,200}$/u
const CONTENT_STYLES = new Set(['heading', 'helper', 'callout'])
const BLOCK_KINDS = new Set<string>(PAGE_DEFINITION_BLOCK_KINDS)
const INPUTS = new Set<string>(PAGE_FIELD_INPUTS)
const FORMATS = new Set<string>(PAGE_VALUE_FORMATS)
const DEPENDENCIES = new Set<string>([
  'entity', 'field', 'relation', 'form', 'view', 'dashboard', 'widget', 'timeline',
  'process', 'action', 'rule', 'control',
])

const optionalExact = (
  value: Record<string, unknown>, required: readonly string[], optional: readonly string[],
): boolean => {
  const allowed = new Set([...required, ...optional])
  return required.every(key => Object.hasOwn(value, key))
    && Object.keys(value).every(key => allowed.has(key))
}
function parseSpan(value: unknown): PageDefinitionSpan | null {
  if (!record(value) || !exact(value, ['desktop', 'tablet', 'phone'])
      || !integer(value.desktop, 1, 4) || !integer(value.tablet, 1, 4)
      || !integer(value.phone, 1, 2)) return null
  return value as unknown as PageDefinitionSpan
}

function parseWhen(value: unknown): PageDefinitionRuleRef | null {
  return record(value) && exact(value, ['rule'])
    && typeof value.rule === 'string' && RULE_REFERENCE.test(value.rule)
    ? { rule: value.rule } : null
}

function parsePresentation(value: unknown): PageFieldPresentation | null {
  if (!record(value) || !optionalExact(value, [], ['input', 'format'])) return null
  if (value.input !== undefined && (typeof value.input !== 'string' || !INPUTS.has(value.input))) return null
  let format: PageFieldFormatting | undefined
  if (value.format !== undefined) {
    if (!record(value.format)
        || !optionalExact(value.format, ['kind'], ['currency', 'precision'])
        || typeof value.format.kind !== 'string' || !FORMATS.has(value.format.kind)
        || (value.format.currency !== undefined
          && (typeof value.format.currency !== 'string' || !CURRENCY.test(value.format.currency)))
        || (value.format.kind === 'currency') !== (value.format.currency !== undefined)
        || (value.format.precision !== undefined && !integer(value.format.precision, 0, 6))) return null
    format = value.format as unknown as PageFieldFormatting
  }
  return {
    ...(value.input === undefined ? {} : { input: value.input as PageFieldInput }),
    ...(format === undefined ? {} : { format }),
  }
}

function parseBlock(value: unknown): PageDefinitionBlock | null {
  if (!record(value) || typeof value.kind !== 'string' || !BLOCK_KINDS.has(value.kind)
      || typeof value.id !== 'string' || !IDENTIFIER.test(value.id)
      || (value.hidden !== undefined && typeof value.hidden !== 'boolean')
      || (value.when !== undefined && parseWhen(value.when) === null)) return null
  const common = ['id', 'kind', 'hidden', 'when']
  const base = {
    id: value.id, kind: value.kind,
    ...(value.hidden === undefined ? {} : { hidden: value.hidden }),
    ...(value.when === undefined ? {} : { when: parseWhen(value.when)! }),
  }
  const key = (name: string) => typeof value[name] === 'string' && IDENTIFIER.test(value[name] as string)
  switch (value.kind) {
    case 'field': {
      if (!optionalExact(value, [...common.slice(0, 2), 'entity', 'field'], ['hidden', 'when', 'presentation'])
          || !key('entity') || !key('field')
          || (value.presentation !== undefined && parsePresentation(value.presentation) === null)) return null
      return { ...base, kind: 'field', entity: value.entity as string, field: value.field as string,
        ...(value.presentation === undefined ? {} : { presentation: parsePresentation(value.presentation)! }) }
    }
    case 'form':
    case 'view': {
      const preset = value[value.kind]
      const optional = value.kind === 'form' ? ['hidden', 'when', 'sections'] : ['hidden', 'when']
      if (!optionalExact(value, [...common.slice(0, 2), 'entity', value.kind], optional)
          || !key('entity') || typeof preset !== 'string'
          || preset.trim() === '' || !NAMED_PRESET.test(preset)) return null
      if (value.kind === 'form' && value.sections !== undefined
          && (!Array.isArray(value.sections) || value.sections.length < 1 || value.sections.length > 20
            || value.sections.some(section => typeof section !== 'string'
              || section.trim() === '' || !NAMED_PRESET.test(section)))) return null
      return { ...base, kind: value.kind, entity: value.entity as string,
        [value.kind]: preset,
        ...(value.kind === 'form' && value.sections !== undefined ? { sections: value.sections } : {}),
      } as PageFormBlock | PageViewBlock
    }
    case 'relation':
      if (!optionalExact(value, [...common.slice(0, 2), 'entity', 'slot'], ['hidden', 'when'])
          || !key('entity') || typeof value.slot !== 'string'
          || value.slot.trim() === '' || !OPAQUE_SLOT.test(value.slot)) return null
      return { ...base, kind: 'relation', entity: value.entity as string, slot: value.slot as string }
    case 'dashboard':
      if (!optionalExact(value, [...common.slice(0, 2), 'dashboard'], ['hidden', 'when']) || !key('dashboard')) return null
      return { ...base, kind: 'dashboard', dashboard: value.dashboard as string }
    case 'timeline':
    case 'process':
      if (!optionalExact(value, [...common.slice(0, 2), 'entity', value.kind], ['hidden', 'when'])
          || !key('entity') || !key(value.kind)) return null
      return { ...base, kind: value.kind, entity: value.entity as string,
        [value.kind]: value[value.kind] as string } as PageTimelineBlock | PageProcessBlock
    case 'actions':
      if (!optionalExact(value, [...common.slice(0, 2), 'entity', 'group'], ['hidden', 'when'])
          || !key('entity') || !['primary', 'secondary', 'bulk'].includes(value.group as string)) return null
      return { ...base, kind: 'actions', entity: value.entity as string,
        group: value.group as PageActionsBlock['group'] }
    case 'content': {
      if (!optionalExact(value, [...common.slice(0, 2), 'style', 'text'], ['hidden', 'when'])
          || typeof value.style !== 'string' || !CONTENT_STYLES.has(value.style)
          || !record(value.text) || !optionalExact(value.text, ['en'], ['fr', 'nl'])) return null
      const translations = value.text as Record<string, unknown>
      if (Object.values(translations).some(text => typeof text !== 'string'
          || text.trim() === '' || text.length > 500 || /[\p{Cc}\p{Cf}]/u.test(text)
          || /<\/?[a-z][^>]*>/iu.test(text))) return null
      return { ...base, kind: 'content', style: value.style as PageContentBlock['style'],
        text: value.text as unknown as PageContentBlock['text'] }
    }
    case 'widget':
      if (!optionalExact(value, [...common.slice(0, 2), 'dashboard', 'item'], ['hidden', 'when'])
          || !key('dashboard') || !key('item')) return null
      return { ...base, kind: 'widget', dashboard: value.dashboard as string, item: value.item as string }
    default: return null
  }
}

/** Parse imported, persisted, AI-authored or MCP-authored JSON at one boundary. */
export function parsePageDefinition(value: unknown): PageDefinition | null {
  if (!record(value)
      || !exact(value, ['schemaVersion', 'id', 'revision', 'surface', 'sections', 'unplaced', 'dependencies'])
      || value.schemaVersion !== PAGE_DEFINITION_SCHEMA_VERSION || value.surface !== 'page'
      || typeof value.id !== 'string' || !PAGE_KEY.test(value.id)
      || !integer(value.revision, 1, Number.MAX_SAFE_INTEGER)
      || !Array.isArray(value.sections) || value.sections.length > 20
      || !Array.isArray(value.unplaced) || value.unplaced.length > 200
      || !Array.isArray(value.dependencies) || value.dependencies.length > 500) return null

  const ids = new Set<string>()
  const takeId = (id: string): boolean => {
    if (ids.has(id)) return false
    ids.add(id)
    return true
  }
  const sections: PageDefinitionSection[] = []
  for (const section of value.sections) {
    if (!record(section) || !exact(section, ['id', 'label', 'description', 'rows'])
        || typeof section.id !== 'string' || !IDENTIFIER.test(section.id) || !takeId(section.id)
        || typeof section.label !== 'string' || section.label.trim() === '' || section.label.length > 120
        || /[\p{Cc}\p{Cf}]/u.test(section.label)
        || !(section.description === null || (typeof section.description === 'string'
          && section.description.length <= 500 && !/[\p{Cc}\p{Cf}]/u.test(section.description)))
        || !Array.isArray(section.rows) || section.rows.length > 20) return null
    const rows: PageDefinitionRow[] = []
    for (const row of section.rows) {
      if (!record(row) || !exact(row, ['id', 'columns'])
          || typeof row.id !== 'string' || !IDENTIFIER.test(row.id) || !takeId(row.id)
          || !Array.isArray(row.columns) || row.columns.length < 1 || row.columns.length > 4) return null
      const columns: PageDefinitionColumn[] = []
      for (const column of row.columns) {
        if (!record(column) || !exact(column, ['id', 'span', 'blocks'])
            || typeof column.id !== 'string' || !IDENTIFIER.test(column.id) || !takeId(column.id)
            || !Array.isArray(column.blocks) || column.blocks.length > 20) return null
        const span = parseSpan(column.span)
        if (!span) return null
        const blocks: PageDefinitionBlock[] = []
        for (const candidate of column.blocks) {
          const block = parseBlock(candidate)
          if (!block || !takeId(block.id)) return null
          blocks.push(block)
        }
        columns.push({ id: column.id, span, blocks })
      }
      /* A row is an authoring group, not a no-wrap CSS line. Columns that do
         not fit the active profile wrap in authored order; this is what lets a
         four-column desktop row become two-by-two on tablet and one-by-one on
         phone without duplicating the document. Each individual span is still
         bounded by `parseSpan`. */
      rows.push({ id: row.id, columns })
    }
    sections.push({ id: section.id, label: section.label, description: section.description, rows })
  }
  const unplaced: PageDefinitionBlock[] = []
  for (const candidate of value.unplaced) {
    const block = parseBlock(candidate)
    if (!block || !takeId(block.id)) return null
    unplaced.push(block)
  }
  const dependencies: PageDefinitionDependency[] = []
  const dependencyIds = new Set<string>()
  for (const dependency of value.dependencies) {
    if (!record(dependency) || !exact(dependency, ['kind', 'key', 'version'])
        || typeof dependency.kind !== 'string' || !DEPENDENCIES.has(dependency.kind)
        || typeof dependency.key !== 'string'
        || (dependency.kind === 'rule' ? !RULE_REFERENCE.test(dependency.key)
          : dependency.kind === 'relation'
            ? dependency.key.trim() === '' || !OPAQUE_SLOT.test(dependency.key)
            : !IDENTIFIER.test(dependency.key))
        || !(dependency.version === null || typeof dependency.version === 'string'
          || (typeof dependency.version === 'number' && Number.isFinite(dependency.version)))) return null
    const identity = `${dependency.kind}:${dependency.key}`
    if (dependencyIds.has(identity)) return null
    dependencyIds.add(identity)
    dependencies.push(dependency as unknown as PageDefinitionDependency)
  }
  return {
    schemaVersion: PAGE_DEFINITION_SCHEMA_VERSION, id: value.id, revision: value.revision as number,
    surface: 'page', sections, unplaced, dependencies,
  }
}

/** Stable document form for previews, diffs and solution artefacts. */
export function canonicalPageDefinition(value: unknown): string {
  const document = parsePageDefinition(value)
  if (!document) throw new Error('PageDefinition v3 is invalid')
  return JSON.stringify({ ...document, dependencies: [...document.dependencies].sort((a, b) =>
    `${a.kind}:${a.key}:${a.version ?? ''}`.localeCompare(`${b.kind}:${b.key}:${b.version ?? ''}`)) })
}

export interface LocatedPageDefinitionBlock {
  section: string
  row: string
  column: string
  block: PageDefinitionBlock
}

/** One deterministic traversal shared by renderers, previews and MCP diffs. */
export function pageDefinitionBlocks(document: PageDefinition): LocatedPageDefinitionBlock[] {
  return document.sections.flatMap(section => section.rows.flatMap(row => row.columns.flatMap(column =>
    column.blocks.filter(block => !block.hidden).map(block => ({
      section: section.id, row: row.id, column: column.id, block,
    })))))
}
